'use client'

import { LoginFormContent } from '@/components/pages/auth/LoginForm'
import { HomeMedia } from '@/components/pages/site/home/sections/home-ui'
import { cn } from '@/lib/utils'
import { createModalStore } from '@/stores/shared/createModalStore'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useSession } from 'next-auth/react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

export const useGuestLoginModal = createModalStore()

/** Set once a guest closes the auto-opened modal, so it doesn't reappear this browser session. */
export const GUEST_LOGIN_DISMISSED_KEY = 'ctxor:guest-login-dismissed'
const AUTO_OPEN_DELAY_MS = 1200
/** Routes where guests are greeted with the modal automatically; everywhere else it opens on demand. */
const AUTO_OPEN_PATHS = new Set(['/'])

// Vertical (9:16) AI-generated shots from Bookstory productions; the surface shows while a clip loads.
const SLIDES = [
  { key: 'marketplace', surface: 'bg-ink', media: '/videos/showcase/stray-cat.mp4' },
  { key: 'sell', surface: 'bg-gradient-to-br from-brand-deep via-brand-deep to-brand-accent/70', media: '/videos/showcase/architect-pitch.mp4' },
  { key: 'hire', surface: 'bg-gradient-to-br from-brand-accent via-brand-glow to-brand-soft', media: '/videos/showcase/anime-office.mp4' },
] as const

/**
 * Higgsfield-style sign-in dialog for guests: rotating showcase on the left, the shared
 * login form on the right. Opened by the header Login button, and once per session on
 * AUTO_OPEN_PATHS while signed out.
 */
export function GuestLoginModal() {
  const t = useTranslations('auth.modal')
  const { status } = useSession()
  const pathname = usePathname()
  const { isOn, openModal, closeModal } = useGuestLoginModal()

  useEffect(() => {
    if (status !== 'unauthenticated' || !AUTO_OPEN_PATHS.has(pathname)) return
    if (sessionStorage.getItem(GUEST_LOGIN_DISMISSED_KEY)) return
    const timer = window.setTimeout(openModal, AUTO_OPEN_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [status, pathname, openModal])

  // A session can appear while the dialog is open (e.g. Google sign-in in another tab).
  useEffect(() => {
    if (status === 'authenticated' && isOn) closeModal()
  }, [status, isOn, closeModal])

  const handleOpenChange = (open: boolean) => {
    if (open) return openModal()
    sessionStorage.setItem(GUEST_LOGIN_DISMISSED_KEY, '1')
    closeModal()
  }

  if (status === 'authenticated') return null

  return (
    <DialogPrimitive.Root open={isOn} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'dark fixed left-1/2 top-1/2 z-50 grid h-[90svh] max-h-[calc(100svh-2rem)] min-h-0 w-[calc(100%-2rem)] max-w-5xl -translate-x-1/2 -translate-y-1/2 overflow-hidden',
            'grid-cols-1 items-stretch rounded-3xl border border-white/10 bg-ink p-2.5 text-white shadow-[0_0_120px_-30px_var(--color-brand-accent)] md:grid-cols-2',
            'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <Showcase />

          <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
            <DialogPrimitive.Close className="absolute right-3 top-3 z-10 rounded-sm p-1 text-white/70 transition hover:text-brand-accent focus-visible:outline-2 focus-visible:outline-brand-soft">
              <X className="size-5" aria-hidden />
              <span className="sr-only">{t('close')}</span>
            </DialogPrimitive.Close>

            <AuthFormScrollRegion>
              <div className="mx-auto w-full max-w-sm">
                <LoginFormContent
                  returnTo={pathname}
                  header={({ codeSent }) => (
                    <div className="mb-6 flex flex-col items-center gap-2 text-center">
                      <DialogPrimitive.Title className="text-3xl font-semibold tracking-tight">{t('title')}</DialogPrimitive.Title>
                      <DialogPrimitive.Description className="text-sm text-white/60">{t('subtitle')}</DialogPrimitive.Description>
                      {/* Hidden while entering a code: the sign-up CTA competes with the step in progress. */}
                      {codeSent ? null : (
                        <Link
                          href="/register"
                          onClick={closeModal}
                          className="mt-5 flex w-full items-center justify-center rounded-xl bg-brand-accent px-5 py-3 text-base font-semibold text-ink shadow-[0_5px_0_0_var(--color-brand-deep)] transition hover:-translate-y-0.5 hover:bg-brand-glow active:translate-y-[3px] active:shadow-none"
                        >
                          {t('signupCta')}
                        </Link>
                      )}
                    </div>
                  )}
                />
              </div>
            </AuthFormScrollRegion>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/** Scrollable form column with a bottom fade when more content sits below the fold. */
function AuthFormScrollRegion({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showBottomFade, setShowBottomFade] = useState(false)

  const syncBottomFade = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const hasOverflow = el.scrollHeight > el.clientHeight + 2
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
    setShowBottomFade(hasOverflow && !atBottom)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    syncBottomFade()
    const observer = new ResizeObserver(syncBottomFade)
    observer.observe(el)
    el.addEventListener('scroll', syncBottomFade, { passive: true })
    return () => {
      observer.disconnect()
      el.removeEventListener('scroll', syncBottomFade)
    }
  }, [syncBottomFade])

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        className="super-thin-scrollbar h-full min-h-0 overflow-y-auto overscroll-contain px-5 py-12 pt-14 sm:px-10 md:px-14 [-webkit-overflow-scrolling:touch] [scrollbar-gutter:stable] [&::-webkit-scrollbar]:size-px [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/25"
      >
        {children}
      </div>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-14 bg-gradient-to-t from-ink via-ink/80 to-transparent',
          showBottomFade ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  )
}

/** Auto-advancing slides; each progress bar's CSS animation end drives the next slide. */
function Showcase() {
  const t = useTranslations('auth.modal.slides')
  const [active, setActive] = useState(0)
  const slide = SLIDES[active]

  return (
    <div className={cn('group/showcase relative hidden h-full min-h-0 overflow-hidden rounded-2xl md:block', slide.surface)}>
      <HomeMedia key={slide.key} src={slide.media} className="absolute inset-0 size-full object-cover" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-7">
        <span className="self-start rounded-md bg-white/15 px-2.5 py-1 text-xs font-semibold backdrop-blur-sm">{t(`${slide.key}.badge`)}</span>
        <p className="font-display text-5xl uppercase leading-[0.9] tracking-tight">{t(`${slide.key}.title`)}</p>
        <p className="max-w-sm text-sm text-white/75">{t(`${slide.key}.body`)}</p>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {SLIDES.map((s, index) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setActive(index)}
              aria-label={t(`${s.key}.title`)}
              aria-current={index === active}
              className="flex flex-col gap-2 text-left text-xs font-medium text-white/50 transition hover:text-white aria-[current=true]:text-white"
            >
              <span className="h-1 overflow-hidden rounded-full bg-white/25">
                {index < active && <span className="block h-full bg-white" />}
                {index === active && (
                  <span
                    key={active}
                    onAnimationEnd={() => setActive((active + 1) % SLIDES.length)}
                    className="block h-full origin-left animate-progress-fill bg-brand-accent group-hover/showcase:[animation-play-state:paused] motion-reduce:animate-none"
                  />
                )}
              </span>
              {t(`${s.key}.title`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
